import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';

const SOURCE_COLORS = [
  '#3b82f6',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#ec4899',
  '#14b8a6',
  '#f97316',
];

const getSourceColor = (source, index) => SOURCE_COLORS[index % SOURCE_COLORS.length];

const pivotByDay = (rows) => {
  const byDay = new Map();
  const sources = new Set();

  rows.forEach(({ day, source, pictures }) => {
    sources.add(source);

    const key = dayjs(day).format('YYYY-MM-DD');
    if (!byDay.has(key))
      byDay.set(key, { day: key });

    byDay.get(key)[source] = (byDay.get(key)[source] || 0) + Number(pictures || 0);
  });

  const sortedDays = Array.from(byDay.values()).sort((a, b) => a.day.localeCompare(b.day));
  const sortedSources = Array.from(sources).sort();

  return { data: sortedDays, sources: sortedSources };
};

export default function DataHealthDailyChart({ rows, loading }) {
  const { t } = useTranslation();
  const { data, sources } = useMemo(() => pivotByDay(rows || []), [rows]);

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="h-72 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-center text-gray-500 dark:text-gray-400">
        {t('No data available')}
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
      <ResponsiveContainer width="100%" height={320}>
        <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="day"
            tickFormatter={(value) => dayjs(value).format('DD MMM')}
            tick={{ fontSize: 12 }}
          />
          <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
          <Tooltip
            labelFormatter={(value) => dayjs(value).format('LL')}
            formatter={(value, name) => [value, name]}
            cursor={{ fill: 'rgba(0,0,0,0.05)' }}
          />
          <Legend />
          {sources.map((source, index) => (
            <Bar
              key={source}
              dataKey={source}
              stackId="sources"
              fill={getSourceColor(source, index)}
              isAnimationActive={false}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
